/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import { failHidden } from './fail';

// import $ from 'mol_data_all';

// function maybeArray<In, Out = In>(type: $.$mol_data_value<In, Out>) {
//     return $.$mol_data_optional($.$mol_data_variant(type, $.$mol_data_array(type)));
// }

// export const s = {
//     str: $.$mol_data_string,
//     bool: $.$mol_data_boolean,
//     num: $.$mol_data_number,
//     variant: $.$mol_data_variant,
//     opt: $.$mol_data_optional,
//     list: $.$mol_data_array,
//     rec: $.$mol_data_record,
//     maybeArray
// } as const;

export type Merge<Intersection> =
    Intersection extends object
    ? {
        [Key in keyof Intersection] : Merge<Intersection[ Key ]>
    }
    : Intersection

// type PartialUpper<Val> = Merge<Partial<Val> & PickUpper<Val, undefined, unknown>>

export type PartialUpper< Val > = Merge<
    Partial< Val >
    &
    Pick< Val, {
        [ Field in keyof Val ]
            : undefined extends Val[Field]
            ? never
            : Field
    }[ keyof Val ] >
>

// type ExtractUpper<Input, Lower, Upper> = {
//     [Field in keyof Input]: Lower extends Input[Field] ? never : Input[Field] extends Upper ? Field : never
// }[keyof Input]

// type PickUpper<Input, Lower, Upper> = Pick<Input, ExtractUpper<Input, Lower, Upper>>

export type RecMetadata = {metadata: Record<string, any>}

export class SchemaTypeError extends Error {
    name = SchemaTypeError.displayName

    static displayName = 'SchemaTypeError'

    static is(obj: Object): obj is SchemaTypeError {
        return (obj && (obj as any).name === this.displayName);
    }
}

function setup<
    Val extends Value,
    Config = never
>(
    value: Val,
    config: Config,
) {
    return Object.assign(value, {
        config,
        Value: null as unknown as ReturnType<Val>
    });
}

function rec<Sub extends Record<string, Value<any>>>(sub: Sub) {
    type Input = PartialUpper<
        {
            [key in keyof Sub]: Parameters<Sub[key]>[0]
        }
    >

    type Output = PartialUpper<
        {
            [key in keyof Sub]: ReturnType<Sub[key]>
        }
    >

    return setup((val: Input) => {
        const res = {} as Output;

        // eslint-disable-next-line guard-for-in
        for (const field in sub) {
            try {
                res[field] = sub[field](val[field]);
            } catch (error) {
                if (! (error instanceof Promise)) {
                    error.message = `[${JSON.stringify(field)}] ${error.message || error}`;
                }
                return failHidden(error);
            }
        }

        return res as Readonly<Output>;
    }, sub);
}

function list<Sub extends Value>(sub: Sub) {
    return (val: readonly Parameters<Sub>[0][]) => {
        if (! Array.isArray(val)) throw new SchemaTypeError('is not an array');

        return val.map((item, index) => {
            try {
                return sub(item);
            } catch (error) {
                if (! (error instanceof Promise)) {
                    error.message = `[${index}] ${error.message || error}`;
                }
                return failHidden(error);
            }
        }) as readonly ReturnType<Sub>[];
    };
}

function num(val: number) {
    if (typeof val === 'number') return val;

    throw new SchemaTypeError('is not a number');
}

function any(val: unknown) {
    return val;
}

function bool(val: boolean) {
    if (typeof val === 'boolean') return val;

    throw new SchemaTypeError('is not a boolean');
}

function boolExt(val: boolean | 'YES') {
    if (typeof val === 'boolean') {
        return val;
    }

    if (val === 'YES') {
        return true;
    }

    throw new SchemaTypeError('is not a boolean or \'YES\'');
}

function undef(val: undefined) {
    if (val === undefined) return val;

    throw new SchemaTypeError('is not a undefined');
}

function str(val: string) {
    if (typeof val === 'string') return val;

    throw new SchemaTypeError('is not a string');
}

type Metadata = Record<string, any>

export type Value<Input = any, Output = any> = ((val: Input) => Output) // & { config?: Metadata }

export class SchemaVariantError extends SchemaTypeError {
    name = SchemaVariantError.displayName

    static displayName = 'SchemaVariantError'

    static is(obj: Object): obj is SchemaVariantError {
        return (obj && (obj as any).name === this.displayName);
    }

    constructor(errors: string | Error[]) {
        super(Array.isArray(errors) ?
            errors.map(err => err.message || err).join(' and ') :
            errors
        );

        if (Array.isArray(errors)) {
            this.stack = errors.map(err => err.stack).join('\n\n');
        }
    }
}

function variant<Sub extends Value[]>(...sub: Sub) {
    const fn: Value<Parameters<Sub[number]>[0], ReturnType<Sub[number]>> = (
        val: Parameters<Sub[number]>[0]
    ) => {
        const errors: SchemaTypeError[] = [];

        for (let index = 0; index < sub.length; index++) {
            const type = sub[index];

            try {
                return type(val) as ReturnType<Sub[number]>;
            } catch (error) {
                if (! (error instanceof Promise)) {
                    error.message = `variant [${index}] ${error.message || error}`;
                }
                if (! (error instanceof SchemaTypeError)) return failHidden(error);
                errors.push(error);
            }
        }

        throw new SchemaVariantError(errors);
    };

    let config = {} as Metadata;
    let hasMetadata = false;

    for (const item of sub) {
        if (item.config) {
            hasMetadata = true;
            config = { ...fn.config, ...item.config };
        }
    }
    if (hasMetadata) {
        fn.config = config;
    }

    return fn;
}

function opt<Sub extends Value>(sub: Sub) {
    return (val: Parameters<Sub>[0] | undefined) => {
        if (val === undefined) return undefined;

        return sub(val) as ReturnType<Sub>;
    };
}

function nullable<Sub extends Value>(sub: Sub) {
    return (val: Parameters<Sub>[0] | null) => {
        if (val === null) return null;

        return sub(val) as ReturnType<Sub>;
    };
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export function wrapper<Pre extends Value, Obj extends { new(val: ReturnType<Pre>): unknown }>(
    pre: Pre,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Obj: Obj
) {
    return (val: Parameters<Pre>[0]) => new Obj(pre(val)) as InstanceType<Obj>;
}

function nullOpt<In, Out = In>(type: Value<In, Out>) {
    return nullable(opt(type));
}

function maybeArray<In, Out = In>(type: Value<In, Out>) {
    return nullOpt(variant(type, list(type)));
}

export const s = {
    str,
    any,
    bool,
    boolExt,
    num,
    variant,
    opt,
    nullOpt,
    nullable,
    list,
    rec,
    undef,
    maybeArray
} as const;

export type Validator<Params> = Value<Params, Params>
